import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { Theme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { buildThemeFromAI, AIThemeProperties, validateAIThemeProperties, loadGoogleFonts } from '../services/themeBuilder';

// Function to load Google Fonts dynamically
// Removed duplicate function

interface ThemeContextType {
  theme: Theme;
  aiThemeProperties: AIThemeProperties | null;
  isCustomTheme: boolean;
  applyThemeFromData: (themeData: any) => void;
  resetToDefaultTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export function DynamicThemeProvider({ children }: ThemeProviderProps) {
  const [aiThemeProperties, setAiThemeProperties] = useState<AIThemeProperties | null>(null);
  const [theme, setTheme] = useState<Theme>(() => buildThemeFromAI()); // Default theme
  const [isCustomTheme, setIsCustomTheme] = useState(false);

  const applyThemeFromData = useCallback((themeData: any) => {
    console.log('Applying theme data:', themeData);
    if (themeData) {
      const validatedProperties = validateAIThemeProperties(themeData);
      console.log('Validated properties:', validatedProperties);
      if (validatedProperties) {
        setAiThemeProperties(validatedProperties);
        setTheme(buildThemeFromAI(validatedProperties));
        setIsCustomTheme(true);
        return;
      }
    }
    // No valid theme data - use default
    console.log('Using default theme');
    setAiThemeProperties(null);
    setTheme(buildThemeFromAI());
    setIsCustomTheme(false);
  }, []);

  const resetToDefaultTheme = () => {
    setAiThemeProperties(null);
    setTheme(buildThemeFromAI());
    setIsCustomTheme(false);
  };

  useEffect(() => {
    if (aiThemeProperties?.typography) {
      const fonts = [
        aiThemeProperties.typography.headingFont,
        aiThemeProperties.typography.bodyFont
      ]
        .filter((font): font is string => Boolean(font))
        .filter((font, index, array) => array.indexOf(font) === index); // Remove duplicates
      loadGoogleFonts(fonts);
    } else {
      loadGoogleFonts([]);
    }
  }, [aiThemeProperties]);

  const contextValue: ThemeContextType = {
    theme,
    aiThemeProperties,
    isCustomTheme,
    applyThemeFromData,
    resetToDefaultTheme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a DynamicThemeProvider');
  }
  return context;
}
